import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { TrainerService } from '@thiememeulenhoff/ng-api-gateway';
import {
  TrainerAnswerMethod,
  TrainerCue,
  TrainerMultipleChoiceAnswer,
  TrainerSessionProgress,
} from '@thiememeulenhoff/types';
import {
  toRequestError,
  withRequestStatus,
} from '../_utils/request-status.feature';

const ANSWER_METHOD: TrainerAnswerMethod = 'MULTIPLE_CHOICE_4';
const DEFAULT_TIMEZONE = 'Europe/Amsterdam';
const CLIENT_SIDE_STATS = {
  reactionTime: 1,
  presentationStartTime: 0,
  presentationDuration: 1,
  sessionTime: 2,
};

type TrainerState = {
  sessionId: string | null;
  cue: TrainerCue | null;
  sessionProgress: TrainerSessionProgress | null;
};

const initialState: TrainerState = {
  sessionId: null,
  cue: null,
  sessionProgress: null,
};

export const TrainerStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withMethods((store, trainerService = inject(TrainerService)) => ({
    async startSession(
      arrangementCode: string,
      wordListId: string,
      context: string,
      timezone = DEFAULT_TIMEZONE,
    ): Promise<void> {
      patchState(store, {
        requestStatus: 'loading',
        sessionId: null,
        cue: null,
        sessionProgress: null,
      });
      try {
        const session = await firstValueFrom(
          trainerService.initializeSession({
            arrangementCode,
            wordListId,
            context,
            answerMethod: ANSWER_METHOD,
            randomOrder: true,
            timezone,
          }),
        );

        patchState(store, {
          requestStatus: 'success',
          sessionId: session.sessionId,
          cue: session.cue,
          sessionProgress: session.sessionProgress,
        });
      } catch (error) {
        patchState(store, {
          requestStatus: toRequestError(error),
        });
        throw error;
      }
    },
    async submitAnswer(answer: TrainerMultipleChoiceAnswer): Promise<void> {
      const sessionId = store.sessionId();
      const cue = store.cue();

      if (sessionId === null || cue === null) {
        throw new Error('Trainer session is not initialized');
      }

      patchState(store, { requestStatus: 'pending' });

      try {
        const progress = await firstValueFrom(
          trainerService.submitResponse({
            ...CLIENT_SIDE_STATS,
            sessionId,
            factId: cue.fact.id,
            givenResponse: answer.text,
            answerMethod: ANSWER_METHOD,
            presentedCueTextIndex: cue.fact.presentedCueTextIndex,
            correct: answer.correct,
            studyTrial: false,
          }),
        );
        patchState(store, {
          requestStatus: 'success',
          sessionId: progress.sessionId,
          cue: progress.cue,
          sessionProgress: progress.sessionProgress,
        });
      } catch (error) {
        patchState(store, { requestStatus: toRequestError(error) });
        throw error;
      }
    },
  })),
);
